import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

interface Bookmark {
    _id: string;
    name: string;
    location?: string;
    city?: string;
    address?: string;
    priceRange?: string;
    minPrice?: number;
    maxPrice?: number;
    featuredImage?: string;
    type?: string; 
    propertyType?: string; 
    itemType: 'property' | 'project';
    slug?: string;
}

interface Stats {
    totalProperties: number;
    activeProperties: number;
    featuredProperties: number;
    forRent: number;
    forSale: number;
}

export default function DevDashboard() {
    const { user, token } = useAuth();
    const [stats, setStats] = useState<Stats | null>(null);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [availablePackages, setAvailablePackages] = useState<any[]>([]);
    const [wishlist, setWishlist] = useState<Bookmark[]>([]);
    const [loading, setLoading] = useState(true);
    const [submittingPkgId, setSubmittingPkgId] = useState<string | null>(null);
    const [selectedPkg, setSelectedPkg] = useState<any>(null);
    const [transactionId, setTransactionId] = useState('');
    const [submittingPurchase, setSubmittingPurchase] = useState(false);
    const [adminUpiId, setAdminUpiId] = useState('admin@upi');

    useEffect(() => {
        if (token) {
            loadData();
        } else {
            setLoading(false);
        }
    }, [token]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsData, profileData, packagesData, wishlistData, settingsData] = await Promise.all([
                api.getStats(token!),
                api.getUserProfile(token!),
                api.getPackages('developer'),
                api.getWishlist(token!),
                api.getSystemSettings()
            ]);

            if (settingsData?.adminUpiId) setAdminUpiId(settingsData.adminUpiId);

            if (!statsData.message) setStats(statsData);
            setUserProfile(profileData);
            setAvailablePackages(packagesData);
            
            const combined: Bookmark[] = [
                ...(wishlistData.wishlist || []).map((p: any) => ({ ...p, itemType: 'property' })),
                ...(wishlistData.projectWishlist || []).map((p: any) => ({ ...p, itemType: 'project' }))
            ];
            setWishlist(combined);
        } catch (error) {
            console.error("Error loading dashboard data:", error);
            toast.error("Dashboard synchronization error.");
        } finally {
            setLoading(false);
        }
    };

    const openCheckout = (pkg: any) => {
        setSelectedPkg(pkg);
        setTransactionId('');
    };

    const closeCheckout = () => {
        setSelectedPkg(null);
        setTransactionId('');
    };

    const handleFreePurchase = async (pkg: any) => {
        if (!token) return;
        setSubmittingPkgId(pkg._id);
        try {
            const res = await api.assignPackage(token, pkg._id);
            const isSuccess = res && (
                res.success === true ||
                res._id ||
                res.assignedPackage ||
                (res.message && res.message.toLowerCase().includes('success')) ||
                (!res.error && !res.message?.toLowerCase().includes('error') && !res.message?.toLowerCase().includes('fail'))
            );
            if (isSuccess) {
                toast.success(`🎉 "${pkg.name}" package activated successfully!`);
                await loadData();
            } else {
                toast.error(res?.message || 'Failed to activate package. Please try again.');
            }
        } catch (err) {
            toast.error('Server error. Could not activate free package.');
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const handlePurchase = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !selectedPkg) return;
        if (!transactionId.trim()) {
            toast.error('Please enter a valid transaction reference number');
            return;
        }

        setSubmittingPurchase(true);
        try {
            await api.purchasePlan(token, {
                packageId: selectedPkg._id,
                name: selectedPkg.name,
                price: selectedPkg.price,
                duration: selectedPkg.period || 'Unlimited',
                users: '1',
                leadAccess: 'Developer Package',
                dailyUnlock: 'Unlimited',
                transactionId
            });
            toast.success('Purchase submitted successfully! Admin will review your transaction.');
            closeCheckout();
        } catch (err) {
            toast.error('Failed to submit purchase request');
        } finally {
            setSubmittingPurchase(false);
        }
    };

    const extractAmount = (priceStr: string) => {
        if (!priceStr) return '';
        const matches = priceStr.toString().match(/\d+/g);
        return matches ? matches.join('') : '';
    };

    const handlePackageInquiry = async (packageId: string) => {
        if (submittingPkgId) return;
        try {
            setSubmittingPkgId(packageId);
            const res = await api.sendPackageInquiry(token!, packageId);
            if (res.success) {
                toast.success("Inquiry transmitted! Our team will contact you shortly.");
            } else {
                toast.error(res.message || "Protocol transmission failed.");
            }
        } catch (error) {
            console.error("Package inquiry error:", error);
            toast.error("Link failure. Try again.");
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const handleRemoveWishlist = async (itemId: string, itemType: 'property' | 'project') => {
        try {
            const res = await api.toggleWishlist(token!, itemId, itemType);
            if (!res.isSaved) {
                setWishlist(prev => prev.filter(b => b._id !== itemId));
                toast.info("Removed from your saved assets");
            }
        } catch (error) {
            toast.error("Process interrupted. Try again.");
        }
    };

    const cleanTitle = (title: string) => {
        let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
        const mapping: Record<string, string> = {
            'LISTINGS SYSTEM': 'CORE',
            'MEDIA SYSTEM': 'DISPLAY',
            'LOCATION + SEARCH': 'LOCALITY',
            'VISIBILITY SYSTEM': 'BOOST',
            'LEAD MGMT': 'ENGAGE',
            'ADVANCED FEATURES': 'PREMIUM'
        };
        for (const [key, value] of Object.entries(mapping)) {
            if (cleaned.toUpperCase().includes(key)) return value;
        }
        return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
    };

    const cleanFeature = (text: string) => {
        return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    };

    const isActive = userProfile?.assignedPackage && 
                    (!userProfile.packageExpiry || new Date(userProfile.packageExpiry) > new Date());

    const daysLeft = userProfile?.packageExpiry ? Math.ceil((new Date(userProfile.packageExpiry).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : Infinity;
    const showRenewalReminder = daysLeft >= 0 && daysLeft <= 7;

    return (
        <UserDashboardLayout>
            <div className="flex flex-col md:flex-row items-center justify-between mb-10 gap-6">
                <div className="animate__animated animate__fadeInLeft">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#0F2A5F] flex items-center justify-center text-white shadow-lg shadow-blue-600/20">
                            <i className="fa-solid fa-building-user text-xl"></i>
                        </div>
                        <h2 className="text-3xl font-black text-black leading-none tracking-tight">Developer Dashboard</h2>
                    </div>
                    <p className="text-black font-bold">Welcome back, <span className="text-[#D4A017] font-black uppercase tracking-widest text-xs">{user?.name}</span>.</p>
                </div>
                <Link to="/developer-add-project" className="group relative overflow-hidden bg-[#0F2A5F] text-white px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all hover:bg-black shadow-xl shadow-slate-950/20 active:scale-95">
                    <span className="relative z-10 flex items-center gap-3">
                        <i className="fa-solid fa-plus-circle text-lg text-[#D4A017]"></i> Launch New Project
                    </span>
                </Link>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[3rem] border border-slate-100 shadow-sm">
                    <div className="w-16 h-16 border-[5px] border-[#D4A017] border-t-transparent rounded-full animate-spin mb-6"></div>
                    <p className="text-black font-black uppercase tracking-[0.2em] text-[10px]">Accessing Real Estate Node...</p>
                </div>
            ) : (
                <>
                    {/* Active Subscription Banner */}
                    <div className="mb-14 animate__animated animate__fadeInUp">
                        {isActive ? (
                            <div className="w-full bg-[#0F2A5F] rounded-[2.5rem] p-8 md:p-10 text-white overflow-hidden relative shadow-2xl border-l-[10px] border-[#D4A017]">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4A017]/10 blur-[100px] rounded-full"></div>
                                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                                        <div className="w-20 h-20 bg-white/5 backdrop-blur-md rounded-3xl flex items-center justify-center text-3xl shadow-inner border border-white/10">
                                            <i className="fa-solid fa-crown text-[#D4A017]"></i>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="text-2xl font-black uppercase tracking-tight italic mb-0 text-[#D4A017]">{userProfile.assignedPackage.name}</h3>
                                                {showRenewalReminder && (
                                                    <span className="px-3 py-1 bg-rose-600 text-white text-[8px] font-black uppercase tracking-[0.2em] rounded-full animate-pulse shadow-lg shadow-rose-600/20">
                                                        Renewal Protocol Required ({daysLeft} Days)
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-white font-bold italic">Premium Partnership Active</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-2 gap-8 lg:gap-16">
                                        <div className="px-6 border-l border-white/10">
                                            <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-2">Project Quota</p>
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-3xl font-black text-[#D4A017]">{userProfile.remainingProjects}</span>
                                                <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Left</span>
                                            </div>
                                        </div>
                                        <div className="px-6 border-l border-white/10">
                                            <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-2">Assigned On</p>
                                            <div className="text-sm font-black text-white uppercase tracking-tighter">
                                                {userProfile.packageStartDate ? new Date(userProfile.packageStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                            </div>
                                        </div>
                                        <div className="px-6 border-l border-white/10">
                                            <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-2">Expires On</p>
                                            <div className="text-sm font-black text-white uppercase tracking-tighter">
                                                {userProfile.packageExpiry ? new Date(userProfile.packageExpiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Unlimited'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="w-full bg-white border border-slate-200 rounded-[2.5rem] p-10 text-center shadow-lg">
                                <i className="fa-solid fa-crown text-4xl text-slate-300 mb-6"></i>
                                <h3 className="text-2xl font-black text-black mb-2 uppercase italic tracking-tight leading-none">Subscription Profile Inactive</h3>
                                <p className="text-black font-bold mb-10 max-w-xl mx-auto">Activate a premium developer node below to bypass listing restrictions and access advanced branding modules.</p>
                                <a href="#pricing-node" className="inline-flex items-center gap-3 px-10 py-4 bg-[#0F2A5F] text-[#D4A017] rounded-full font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:scale-105 transition-all">
                                    Explore Plan Protocol <i className="fa-solid fa-chevron-down"></i>
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-16">
                        {[
                            { label: 'Infrastructure', value: stats?.totalProperties ?? 0, icon: 'fa-building', color: 'text-[#0F2A5F]' },
                            { label: 'Active Node', value: stats?.activeProperties ?? 0, icon: 'fa-signal', color: 'text-emerald-500' },
                            { label: 'Elite List', value: stats?.featuredProperties ?? 0, icon: 'fa-star', color: 'text-[#D4A017]' },
                            { label: 'Resale System', value: stats?.forSale ?? 0, icon: 'fa-tag', color: 'text-rose-500' },
                            { label: 'Rental Grid', value: stats?.forRent ?? 0, icon: 'fa-key', color: 'text-blue-500' },
                        ].map((s, i) => (
                            <div className="bg-white p-8 rounded-[2rem] border border-slate-100 hover:shadow-2xl transition-all group" key={i}>
                                <div className={`w-12 h-12 bg-slate-50 ${s.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all shadow-inner`}>
                                    <i className={`fa-solid ${s.icon} text-lg`}></i>
                                </div>
                                <h3 className="text-3xl font-black text-black dark:text-white mb-1 italic tabular-nums leading-none tracking-tighter">{s.value}</h3>
                                <p className="text-[10px] font-black text-black uppercase tracking-widest leading-tight">{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Pricing Grid Section */}
                    <div id="pricing-node" className="mb-20">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-black text-black uppercase italic tracking-tight mb-3">Partnership Protocol</h2>
                            <div className="w-20 h-1.5 bg-[#D4A017] mx-auto rounded-full"></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {availablePackages.map((pkg) => (
                                <div 
                                    key={pkg._id} 
                                    className={`group relative flex flex-col bg-white rounded-[2.5rem] shadow-xl border border-[#D4A017]/10 hover:border-[#D4A017]/40 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${pkg.popular ? 'scale-[1.02] z-10 border-[#D4A017]/40 shadow-2xl' : ''}`}
                                >
                                    {pkg.popular && (
                                        <div className="absolute top-6 -right-12 bg-[#D4A017] text-white text-[8px] font-black uppercase tracking-widest px-14 py-2 rotate-45 shadow-lg z-10 animate__animated animate__fadeIn">
                                            Elite
                                        </div>
                                    )}

                                    <div className="p-8 border-b border-gray-100 relative overflow-hidden bg-[#0F2A5F] text-white">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg shadow-lg mb-6" style={{ backgroundColor: `${pkg.color}20`, color: pkg.color }}>
                                            <i className={`fa-solid ${pkg.icon || 'fa-building'}`}></i>
                                        </div>
                                        <h3 className="text-2xl font-black tracking-tight mb-1 text-[#D4A017] uppercase italic leading-none">{pkg.name}</h3>
                                        <p className="text-[11px] font-bold text-gray-400 mb-6 line-clamp-1 italic">{pkg.tagline}</p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black tracking-tighter text-white">₹{pkg.price}</span>
                                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">/ {pkg.period}</span>
                                        </div>
                                    </div>

                                    <div className="p-8 flex-grow bg-gray-50/30">
                                        {pkg.categories?.map((cat: any, ci: number) => (
                                            <div key={ci} className="mb-6 last:mb-0">
                                                <h4 className="text-[11px] font-black text-black uppercase tracking-widest mb-3 border-b border-gray-200 pb-1">{cleanTitle(cat.title)}</h4>
                                                <ul className="space-y-3">
                                                    {cat.features.map((feat: string, fi: number) => {
                                                        const isAvailable = !feat.includes('❌');
                                                        const cleanText = cleanFeature(feat);
                                                        return (
                                                            <li key={fi} className={`flex items-start gap-2 ${isAvailable ? '' : 'opacity-30'}`}>
                                                                <i className={`fa-solid ${isAvailable ? 'fa-circle-check text-[#D4A017]' : 'fa-circle-xmark text-gray-400'} text-xs mt-1`}></i>
                                                                <span className="text-[13px] font-bold text-black leading-tight italic">{cleanText}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="px-6 py-4 bg-white border-t border-gray-100">
                                        {(Number(pkg.price) === 0) ? (
                                            <button
                                                onClick={() => handleFreePurchase(pkg)}
                                                disabled={submittingPkgId === pkg._id}
                                                className="w-full py-3.5 rounded-full text-sm font-bold transition-all shadow-lg active:scale-95 disabled:opacity-70 bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2"
                                            >
                                                {submittingPkgId === pkg._id ? (
                                                    <><i className="fa-solid fa-circle-notch fa-spin"></i> Activating...</>
                                                ) : (
                                                    <><i className="fa-solid fa-bolt"></i> Get Started Free</>
                                                )}
                                            </button>
                                        ) : (
                                            <button 
                                                onClick={() => openCheckout(pkg)}
                                                className={`w-full py-3.5 rounded-full text-sm font-bold transition-all shadow-lg active:scale-95 disabled:opacity-70 ${pkg.popular ? 'bg-[#D4A017] text-white hover:bg-[#8b4513]' : 'bg-[#0F2A5F] text-white hover:bg-black'}`}
                                            >
                                                Purchase via UPI
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Saved Assets Section */}
                    {wishlist.length > 0 && (
                        <div className="mb-20 animate__animated animate__fadeInUp">
                            <div className="flex items-center justify-between mb-10 border-b border-gray-100 pb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[#0F2A5F] text-[#D4A017] rounded-2xl flex items-center justify-center shadow-lg">
                                        <i className="fa-solid fa-heart-pulse text-xl"></i>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-black text-black dark:text-white uppercase italic tracking-tight leading-none">Saved Asset Matrix</h2>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">High-Potential Performance Nodes</p>
                                    </div>
                                </div>
                                <Link to="/bookmark-list" className="text-[10px] font-black text-[#0F2A5F] dark:text-[#D4A017] uppercase tracking-widest hover:underline flex items-center gap-2 group">
                                    Full Vault <i className="fa-solid fa-arrow-right-long group-hover:translate-x-1 transition-transform"></i>
                                </Link>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                <AnimatePresence>
                                    {wishlist.slice(0, 4).map((item) => (
                                        <motion.div 
                                            key={item._id}
                                            layout
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            className="group bg-white rounded-[2.5rem] overflow-hidden border border-gray-100 hover:border-[#D4A017]/30 transition-all shadow-sm hover:shadow-2xl flex flex-col h-full relative"
                                        >
                                            <div className="relative h-48 overflow-hidden">
                                                <img 
                                                    src={getImageUrl(item.featuredImage)} 
                                                    alt={item.name} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                                />
                                                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                                                    <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${item.itemType === 'project' ? 'bg-[#D4A017] text-white shadow-lg shadow-amber-500/20' : 'bg-[#0F2A5F] text-white shadow-lg shadow-blue-500/20'}`}>
                                                        {item.itemType}
                                                    </span>
                                                </div>
                                                <button 
                                                    onClick={() => handleRemoveWishlist(item._id, item.itemType)}
                                                    className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-rose-500 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"
                                                >
                                                    <i className="fa-solid fa-xmark text-xs"></i>
                                                </button>
                                            </div>
                                            
                                            <div className="p-6 flex flex-col flex-grow">
                                                <h3 className="text-sm font-black text-black mb-1 truncate uppercase italic tracking-tight">{item.name}</h3>
                                                <div className="flex items-center gap-1.5 text-gray-400 font-bold text-[9px] mb-4">
                                                    <i className="fa-solid fa-location-dot text-[#D4A017]"></i>
                                                    <span className="truncate">{item.city || 'Location N/A'}</span>
                                                </div>

                                                <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                                                    <div className="text-xs font-black text-[#0F2A5F]">
                                                        {item.priceRange || (item.minPrice ? `₹${(item.minPrice/10000000).toFixed(2)} Cr` : 'Call')}
                                                    </div>
                                                    <Link 
                                                        to={item.itemType === 'project' ? `/project-details/${item.slug || item._id}` : `/property-details/${item._id}`}
                                                        className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 hover:bg-[#0F2A5F] hover:text-white transition-all flex items-center justify-center"
                                                    >
                                                        <i className="fa-solid fa-chevron-right text-[10px]"></i>
                                                    </Link>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </div>
                    )}


                </>
            )}

            {/* Checkout & QR Payment Modal */}
            <AnimatePresence>
                {selectedPkg && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
                    <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeCheckout}
                    className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                    />
                    <motion.div 
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-2xl bg-white rounded-xl p-6 shadow-2xl overflow-hidden"
                    >
                    <div className="flex justify-between items-center w-full mb-4">
                        <div>
                        <h2 className="text-2xl font-bold text-slate-900">Secure Payment</h2>
                        <p className="text-sm text-slate-500 mt-1">Pay via UPI to complete purchase</p>
                        </div>
                        <button
                        onClick={closeCheckout}
                        className="w-10 h-10 rounded-md bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600 transition-all font-bold flex items-center justify-center"
                        >
                        <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6">
                        {/* QR Code Section */}
                        <div className="md:w-1/2 bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center">
                        <p className="text-sm font-bold text-slate-700 mb-2">Scan QR Code to Pay</p>
                        <div className="text-xs font-medium text-slate-500 mb-4 bg-white px-4 py-1.5 rounded-md border border-slate-200 shadow-sm select-all">
                            UPI ID: {adminUpiId}
                        </div>

                        <div className="w-36 h-36 bg-white p-2 border border-slate-200 rounded-xl shadow-sm mb-4 flex items-center justify-center">
                            <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(
                                `upi://pay?pa=${adminUpiId}&pn=My%20Property%20Guru&am=${extractAmount(
                                selectedPkg.price
                                )}&cu=INR`
                            )}`}
                            alt="UPI QR Code"
                            className="w-full h-full object-contain"
                            />
                        </div>

                        <div className="flex items-center justify-center gap-2 mt-auto">
                            <span className="text-sm font-medium text-slate-500">Amount to pay:</span>
                            <span className="text-2xl font-black text-[#0F2A5F]">₹{extractAmount(selectedPkg.price)}</span>
                        </div>
                        </div>

                        {/* Form Section */}
                        <div className="md:w-1/2 flex flex-col justify-center">
                        <form onSubmit={handlePurchase} className="w-full space-y-4">
                            <div className="space-y-2">
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Transaction ID (UTR)</label>
                            <input
                                type="text"
                                required
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                placeholder="Enter 12-digit UTR Number"
                                className="w-full px-4 py-3 rounded-md border border-slate-300 focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] outline-none bg-white text-slate-900 font-medium text-sm transition-all shadow-sm"
                            />
                            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">After successful payment via the QR code or UPI ID, please enter the 12-digit transaction reference number (UTR) to authenticate your payment.</p>
                            </div>

                            <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={closeCheckout}
                                className="flex-1 py-3.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-sm transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submittingPurchase}
                                className="flex-1 py-3.5 rounded-md bg-[#0F2A5F] text-white hover:bg-black font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70"
                            >
                                {submittingPurchase ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Submitting...</> : 'Submit Payment'}
                            </button>
                            </div>
                        </form>
                        </div>
                    </div>
                    </motion.div>
                </div>
                )}
            </AnimatePresence>
        </UserDashboardLayout>
    );
}
